/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
  HStack,
  Text,
  Icon,
  Stack,
  Button,
  Link,
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Heading,
  Switch,
  ModalFooter,
} from '@chakra-ui/react';
import { GiCookie } from 'react-icons/gi';
import { useHistory } from 'react-router-dom';
import { useDetectMobile, useDetectTablet } from '../../../helpers/detectDevice';
import { useRouteHelper } from '../../../hooks/routesHelpers';
import { useTranslation } from 'react-i18next';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { LocalStorageKeys, readLocalStorage, setLocalStorage } from '../../../utils/localStorageHelper';

interface IMoreInfoCookieModal {
  isInfoCookieOpen: boolean;
  onCloseInfoCookie: () => void;
  onInfoCookieOpen: () => void;
  handleAcceptAll: () => void;
  handleRefuse: () => void;
  handleSaveAndContinue: () => void;
}

export default function MoreInfoCookieModal({
  isInfoCookieOpen,
  onCloseInfoCookie,
  onInfoCookieOpen,
  handleAcceptAll,
  handleRefuse,
  handleSaveAndContinue,
}: IMoreInfoCookieModal) {
  const { t } = useTranslation();
  const accordions = [
    {
      title: t('cookieBannerSettings.necessary'),
      content: t('cookieBannerSettings.necessaryContent'),
      isDefaultChecked: true,
      isDisabled: true,
      handleChangeSwitch: () => {},
    },
    {
      title: t('cookieBannerSettings.interactions'),
      content: t('cookieBannerSettings.interactionsContent'),
      isDefaultChecked: readLocalStorage(LocalStorageKeys.InteractionCookies, undefined),
      handleChangeSwitch: (value: boolean) => {
        setLocalStorage(LocalStorageKeys.InteractionCookies, value);
      },
    },
    {
      title: t('cookieBannerSettings.improvingExperience'),
      content: t('cookieBannerSettings.improvingExperienceContent'),
      isDefaultChecked: readLocalStorage(LocalStorageKeys.ExperienceCookies, undefined),
      handleChangeSwitch: (value: boolean) => {
        setLocalStorage(LocalStorageKeys.ExperienceCookies, value);
      },
    },
    {
      title: t('cookieBannerSettings.measurements'),
      content: t('cookieBannerSettings.measurementsContent'),
      isDefaultChecked: readLocalStorage(LocalStorageKeys.MeasurementCookies, undefined),
      handleChangeSwitch: (value: boolean) => {
        setLocalStorage(LocalStorageKeys.MeasurementCookies, value);
      },
    },
  ];
  return (
    <>
      <Button
        fontSize={{ base: '14px', lg: '16px' }}
        onClick={onInfoCookieOpen}
        colorScheme="secondary"
        variant="outline"
        isFullWidth
        py="1.5em"
        mb={{ base: '1em', md: '.5em', lg: '0' }}
      >
        {t('cookieBanner.moreInfo')}
      </Button>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isInfoCookieOpen}
        onClose={onCloseInfoCookie}
        isCentered
        size={isMobileOnly ? 'sm' : isTablet ? '2xl' : '4xl'}
      >
        <ModalOverlay />
        <ModalContent px={{ base: '.4em', md: '.5em', lg: '1em' }} py={{ base: '1em', md: '1em', lg: '2em' }} position="relative">
          <ModalHeader>
            <Stack>
              <Heading fontSize={{ base: '14px', md: '20px', lg: '24px' }}>{t('cookieBannerSettings.title')}</Heading>
              <Text fontSize={{ base: '14px', md: '20px', lg: '18px' }} fontWeight="light">
                {t('cookieBannerSettings.followingPanel')}
              </Text>
            </Stack>
          </ModalHeader>
          <ModalBody px={{ base: '.4em', md: '.5em', lg: '1em' }} py={{ base: '1em', md: '1em', lg: '2em' }}>
            <Stack spacing={8}>
              <HStack w="100%" justifyContent="flex-end">
                <Button colorScheme="secondary" variant="outline" py="1.5em" fontSize={{ base: '14px', lg: '16px' }} onClick={handleRefuse}>
                  {t('cookieBanner.refuse')}{' '}
                </Button>
                <Button colorScheme="secondary" variant="solid" py="1.5em" fontSize={{ base: '14px', lg: '16px' }} onClick={handleAcceptAll}>
                  {t('cookieBanner.accept')}{' '}
                </Button>
              </HStack>
              <Accordion allowMultiple>
                {accordions?.map((accordion) => (
                  <AccordionItem key={accordion.title} fontSize={{ base: '14px', lg: '16px' }}>
                    <HStack spacing="1em">
                      <AccordionButton py="1em">
                        <Box flex="1" textAlign="left" fontSize={{ base: '14px', lg: '16px' }}>
                          {accordion.title}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <Switch
                        colorScheme="accentGreen"
                        size="lg"
                        defaultChecked={accordion?.isDefaultChecked}
                        isDisabled={accordion.isDisabled}
                        onChange={(e) => accordion.handleChangeSwitch(e.target.checked)}
                      />
                    </HStack>
                    <AccordionPanel pb={4}>{accordion.content}</AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </Stack>
          </ModalBody>
          <ModalFooter position="sticky" bottom="0" py="0" bg="white">
            <HStack w="100%" justifyContent="space-between">
              <Button colorScheme="secondary" variant="outline" py="1.5em" fontSize={{ base: '14px', lg: '16px' }} onClick={onCloseInfoCookie}>
                {t('cookieBannerSettings.saveAndGoBack')}
              </Button>
              <Button colorScheme="secondary" variant="solid" py="1.5em" fontSize={{ base: '14px', lg: '16px' }} onClick={handleSaveAndContinue}>
                {t('cookieBannerSettings.saveAndContinue')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
